import {extractErrorMessage, timeZone, useGetTtaAuditSummariesQuery, useSearchTtasQuery} from "@ots-frontend/ots-data";
import {skipToken} from "@reduxjs/toolkit/query";
import {useMemo, useState} from "react";
import {ConnectedTicketAuditDialog} from "../../../audit/connected/ConnectedTicketAudit";
import {containerTicketId, type ContainerTicketParty, toContainerTicketEntries} from "../../../container-status/container-details-model";
import {ActivityAttachmentsDialog} from "../../../driver-activity/ActivityAttachmentsDialog";
import type {ActivityEntry} from "../../../driver-activity/model/activity-types";
import type {OtsBrand, OtsColorSchemePreference} from "../../../theme/OtsTheme";
import {MapInfoSection} from "../MapInfoPanel";
import type {RenderMapEntity} from "../map-panel";
import {newestSiteTickets, SITE_TICKET_DAYS, siteTicketSearch} from "../site-tickets";
import {SiteTicketList} from "../SiteTicketList";

export type ConnectedSiteRecentTicketsProps = {
  siteId: string;
  /** Whether rows name the hauler (HubGo) or the driver (DispatchGo). */
  party?: ContainerTicketParty;
  renderEntity?: RenderMapEntity;
  /** Opens a ticket's profile; the app owns the route. */
  onOpenTicket?: (tta: {id: string; database?: string}) => void;
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
};

/**
 * The site panel's latest tickets: the server keeps the newest few across every connected database, audit scores load
 * for just those, and the audit and attachments open in place as on the container panel.
 */
export const ConnectedSiteRecentTickets = ({siteId, party, renderEntity, onOpenTicket, brand, colorScheme}: ConnectedSiteRecentTicketsProps) => {
  // When the panel opened, not the map's ticking clock, which would search again every minute.
  const [openedAt] = useState(() => Date.now());
  const ttas = useSearchTtasQuery(siteTicketSearch(siteId, openedAt));
  const auditTargets = useMemo(() => (ttas.currentData ?? []).map((tta) => ({objectId: tta.id, tenantCode: tta.database})), [ttas.currentData]);
  const audits = useGetTtaAuditSummariesQuery(auditTargets.length ? {targets: auditTargets} : skipToken);
  const entries = useMemo(() => newestSiteTickets(toContainerTicketEntries(ttas.currentData, audits.data)), [ttas.currentData, audits.data]);

  const [auditEntry, setAuditEntry] = useState<ActivityEntry>();
  const [attachments, setAttachments] = useState<{entry: ActivityEntry; index: number}>();

  return <>
    <MapInfoSection
      title="Recent tickets"
      aside={<span className="text-[11px] text-[var(--ots-text-muted)]">Last {SITE_TICKET_DAYS} days</span>}>
      <SiteTicketList
        entries={entries}
        siteId={siteId}
        loading={ttas.isFetching && !ttas.currentData}
        error={ttas.error && extractErrorMessage(ttas.error, "Please try again.")}
        onRetry={() => void ttas.refetch()}
        timeZone={timeZone.value}
        party={party}
        renderEntity={renderEntity}
        onOpenTicket={onOpenTicket && ((entry) => entry.tta && onOpenTicket(entry.tta))}
        onOpenAudit={(entry) => setAuditEntry(entry)}
        onOpenAttachments={(entry) => setAttachments({entry, index: 0})}/>
    </MapInfoSection>
    <ConnectedTicketAuditDialog
      open={!!auditEntry?.tta}
      onClose={() => setAuditEntry(undefined)}
      subtitle={auditEntry && [containerTicketId(auditEntry), auditEntry.pickup?.personName].filter(Boolean).join(" · ")}
      ttaId={auditEntry?.tta?.id ?? ""}
      tenantCode={auditEntry?.tta?.database}
      warnings={auditEntry?.warnings}
      allowRun
      brand={brand}
      colorScheme={colorScheme}/>
    <ActivityAttachmentsDialog
      entry={attachments?.entry}
      index={attachments?.index ?? 0}
      onIndexChange={(index) => setAttachments((current) => current && {...current, index})}
      onClose={() => setAttachments(undefined)}
      timeZone={timeZone.value}
      brand={brand}
      colorScheme={colorScheme}/>
  </>;
};
